<script lang="ts">
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import type { Image } from "#gen/bundles/v1/common_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import type { AssetLoad } from "#lib/api/bundles.js";
	import Icon from "#lib/components/Icon.svelte";
	import TokenizedImage from "#lib/components/TokenizedImage.svelte";
	import TokenizedText from "#lib/components/TokenizedText.svelte";
	import { getAttemptedIconMatch } from "#lib/components/flow/editor/external/advancedDestinations/utils.js";
	import type { ModuleItemProps } from "#lib/components/flow/viewer/types.js";
	import { getAssetRef, getIconUrl, getShortName, getText } from "#lib/components/flow/viewer/utils.js";
	import { parseIconUrl } from "#lib/icons.js";
	import { FALLBACK_ACTION_LABEL, defaultActionLabels } from "#lib/stores/suite.js";
	import { getPrimaryIcon } from "#lib/theme.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import Button from "#lib/ui/Button/index.js";
	import type { Writable } from "svelte/store";

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const assets: Writable<Record<string, AssetLoad>> = getContext("assets");
	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");

	const { suite, module, layoutName, actionEnabled = true, mode, classes, isConversionModule, onShow, onEdit }: ModuleItemProps = $props();
	const ref = $derived(getAssetRef($suite, module));
	const brandIcon = $derived($brandKit ? getPrimaryIcon($brandKit!) : (undefined as Image | undefined));
	const iconUrl = $derived(getIconUrl($assets, module.key, ref, mode));
	const text = $derived(getText($assets, module.key, ref, mode));

	function handleEdit() {
		if ($assets[module.key]) {
			onEdit?.($assets[module.key]);
		}
	}

	function handleShow() {
		onShow?.(module);
	}
</script>

{#if isConversionModule}
	{@const text = getShortName($assets, module.key, ref, mode) || defaultActionLabels.get($suite.bundleType) || FALLBACK_ACTION_LABEL}
	<Button
		color="none"
		data-flow-button
		data-action-name={mode === Mode.EDITING ? "edit-module" : "show-module"}
		onclick={mode === Mode.EDITING ? handleEdit : handleShow}
		class={twMerge("flex items-center gap-1", layoutName ? "btn-user-primary" : "btn-consumer-primary", "border border-gray-300 bg-white text-gray-900 hover:bg-gray-100", classes.buttonClass, classes.outlineClass, classes.readOnlyClass)}
		disabled={!actionEnabled}
	>
		<TokenizedText provider={$tokenProvider} {text} {mode} />
	</Button>
{:else}
	<button data-flow-button type="button" onclick={mode === Mode.EDITING ? handleEdit : handleShow} class={twMerge("flex w-full items-center justify-center gap-2 rounded-2xl bg-white p-2.5", classes.borderClass, classes.outlineClass, classes.readOnlyClass)} class:!opacity-50={ref?.disabled}>
		<div class="relative flex-none overflow-hidden rounded-lg">
			{#if iconUrl && !parseIconUrl(iconUrl)}
				<TokenizedImage provider={$tokenProvider} src={iconUrl} {mode} imgClass="h-8 w-8" badgeClass="hidden" placeholderClass="h-4 w-4" />
			{:else if brandIcon?.url}
				<TokenizedImage provider={$tokenProvider} src={brandIcon.url} {mode} imgClass="h-8 w-8" badgeClass="hidden" placeholderClass="h-4 w-4" />
			{:else}
				{@const icon = getAttemptedIconMatch(ref)}
				{#if icon}
					<Icon iconSet={icon.iconSet} name={icon.icon} class="h-6 w-6" />
				{/if}
			{/if}
		</div>
		<div class="grow text-left text-sm font-semibold text-gray-900">
			<TokenizedText provider={$tokenProvider} {text} {mode} />
		</div>
		<div class="flex h-8 w-8 flex-none items-center justify-center">
			<Icon name="chevron-right" class="h-5 w-5 text-gray-400" />
		</div>
	</button>
{/if}
